import styles from "./savings.module.css"
import React from "react"
import * as finance from "shared/finance"
import * as util from "activities/util"
import * as shared from "activities/components/shared"
import * as inputs from "activities/components/inputs"
import * as callouts from "activities/components/callouts"
import BarChart from "activities/components/charts/bar"
import AccessibilityTable from "activities/components/shared/accessibility-table"

// the math in here is based off this formula:
// [ P(1+r/n)^(nt) ] + [ PMT × (((1 + r/n)^(nt) - 1) / (r/n)) × (1+r/n) ]
// from this website: https://www.thecalculatorsite.com/articles/finance/compound-interest-formula.php?page=2

// some calculators use a formula depending on the deposits being made at the beginning of the period, and some at the end, which results in calculators giving different answers. the most common result seemed to be using the formula for deposits made at the beginning of the period, so I went with that one.

const savings = (principal, payment, years, rate) =>
  finance.amountWithCompoundInterest(principal, years, rate) + finance.amountWithCompoundInterestOnContributions(payment, years, rate)

const interest = (principal, payment, years, rate) => finance.getCompoundInterest(principal, payment, years, rate)

const yearsArrayForChart = (years) => Array.from(Array(years + 1))
const getDataForRecharts = (principal, payment, years, rate) =>
  yearsArrayForChart(years).map((year, i) => ({
    year: i,
    savings: savings(principal, payment, i, rate) - interest(principal, payment, i, rate),
    // ^^this is savings - interest, not total savings
    interest: interest(principal, payment, i, rate),
    total: savings(principal, payment, i, rate)
  }))

const getChartData = (principal, payment, timeInYears, rate) => ({
  legend: {
    x: {
      label: "Years",
      key: "year"
    },
    y: {
      label: "Savings",
      type: "currency"
    }
  },
  fields: {
    year: {type: "number"},
    total: {type: "currency", tooltip: {title: "Total", order: 0}},
    savings: {type: "currency", barOrder: 0, tooltip: {title: "Contributed", order: 2, color: "color-1"}},
    interest: {type: "currency", barOrder: 1, tooltip: {title: "Interest", order: 1, color: "color-4"}}
  },
  values: {
    x: yearsArrayForChart(timeInYears),
    y: getDataForRecharts(principal, payment, timeInYears, rate)
  }
})

export default shared.ActivityFactory(
  ({getProps, getState, updateState}) => {
    const {principal, payment, years, rate, resetKey} = getState()
    const {id, theme} = getProps()
    const chartData = getChartData(principal, payment, years, rate / 100)

    const accessibilityTableHeadings = [
      {
        name: "Years",
        key: "years"
      },
      {
        name: "Savings",
        key: "savings"
      },
      {
        name: "Interest",
        key: "interest"
      },
      {
        name: "Contributed",
        key: "contributed"
      }
    ]

    const accessibilityTableData = chartData.values.y.map((row) => ({
      years: row.year,
      savings: util.formatDollarsNoCents(row.savings),
      interest: util.formatDollarsNoCents(row.interest),
      contributed: util.formatDollarsNoCents(row.total)
    }))

    const accessibilityTableLabel = "Savings Summary"

    return (
      <div>
        <shared.Content>
          <shared.SummaryCalcContainer>
            <shared.InputsContainer>
              <inputs.CalcInput id={id} title="Starting Amount" onChange={updateState("principal", util.fromString)} initialValue={principal} resetKey={resetKey} />
              <inputs.RangeInput
                id={id}
                title="Monthly Savings"
                onChange={updateState("payment", util.fromString)}
                initialValue={payment}
                min={0}
                max={3000}
                step={100}
                units="$"
                resetKey={resetKey}
              />
              <inputs.RangeInput
                id={id}
                title="Years to Save"
                onChange={updateState("years", util.fromString)}
                initialValue={years}
                min={5}
                max={50}
                step={5}
                units="years"
                valueType="years-full"
                resetKey={resetKey}
              />
              <inputs.RangeInput
                id={id}
                title="Interest Rate"
                onChange={updateState("rate", parseFloat)}
                initialValue={rate}
                min={0}
                max={8}
                step={0.25}
                units="%"
                valueType="percent"
                resetKey={resetKey}
              />
            </shared.InputsContainer>
            <shared.ChartContainer>
              <BarChart data={chartData} theme={theme} rowcount={3} aria-label="Bar chart demonstrating how monthly savings grow over time." />
            </shared.ChartContainer>
            <shared.SummaryContainer className={styles["summary"]}>
              <callouts.Callout>
                <callouts.Detail
                  label="Interest Earned"
                  result={util.formatDollarsNoCents(util.ifNegative(finance.getCompoundInterest(principal, payment, years, rate / 100)))}
                  dataTestId="interest-earned"
                />
                <callouts.Detail
                  label="Total Saved"
                  result={util.formatDollarsNoCents(util.ifNegative(finance.getTotalWithCompoundInterest(principal, payment, years, rate / 100)))}
                  dataTestId="total-saved"
                />
              </callouts.Callout>
            </shared.SummaryContainer>
          </shared.SummaryCalcContainer>
        </shared.Content>

        <AccessibilityTable headings={accessibilityTableHeadings} data={accessibilityTableData} label={accessibilityTableLabel} />
      </div>
    )
  },
  {
    stateFilter: (state) => {
      const {principal, payment, years, rate} = state
      return Object.assign(state, {
        interestEarned: util.round(finance.getCompoundInterest(principal, payment, years, rate)),
        totalSaved: util.round(finance.getTotalWithCompoundInterest(principal, payment, years, rate))
      })
    },
    defaultProps: {
      id: "savings-calculator",
      className: "savings-calculator",
      title: "How Much Can You Save?",
      subtitle: "Adjust the inputs to see how much your monthly savings contributions will grow with interest."
    },
    defaultState: {
      principal: 5000,
      payment: 100,
      years: 30,
      rate: 2.5
    }
  }
)
